import {isUndefined} from 'lodash';
import React, {forwardRef, useCallback, useRef} from 'react';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ItemIndexes} from '../../../../../../srcCommon/helpers/itemIndexHelpers';
import {CellsConversationSearchResultsContent} from '../../../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {CellsConversationsDragItem} from '../../../../../helpers/conversationCellHelpers';
import {ConversationSearchResultModel} from '../../../../../models/api/conversationSearchResultModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {CellsPanelConversationsPageStateWithSearchResults} from '../../../../../store/panels/cells/cellsPanelState';
import {
  InboxesConversationsParameters,
  InboxesParametersActivity,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsIdSelected,
  findOnlySelectedId,
  InboxesSelection,
  normalInboxesSelection,
} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ReadStatusInfoBubble} from '../../../../infoBubbles/readStatusInfoBubble';
import {ConversationCellClickEventHandler} from '../../conversationCell';
import {ConversationSearchResultCellContainer} from '../../conversationSearchResultCellContainer';
import {CellsConversationsChildrenProps} from '../cellsConversations';
import {makeCellsItemsBase} from '../cellsItemsBase';

/*
 * Props.
 */

interface CellsConversationsSearchResultsProps extends CellsConversationsChildrenProps {
  page: CellsPanelConversationsPageStateWithSearchResults;
  content: CellsConversationSearchResultsContent;
  selectedDragItem: CellsConversationsDragItem;
  selectedDragMessages: ReadonlyArray<MessageModel>;
}

/*
 * Component.
 */

const CellsItemsBase = makeCellsItemsBase<number>();
export const CellsConversationsSearchResults = forwardRef<
  HTMLDivElement,
  CellsConversationsSearchResultsProps
>((props, ref) => {
  const {page, content, buildParametersPath, onParametersChange, onDoubleClick} = props;
  const {parameters} = page;
  const {conversationSearchResults, conversationIndexes, conversationIds} = content;

  /*
   * Callbacks.
   */

  const buildSelectionParameters = useCallback(
    (selection: InboxesSelection<number>): InboxesConversationsParameters => ({
      ...parameters,
      selection,
      activity: findSearchActivityForConversationSelection(
        conversationSearchResults,
        conversationIndexes,
        selection,
      ),
    }),
    [parameters, conversationSearchResults, conversationIndexes],
  );

  const buildConversationPath = useCallback(
    (conversationId: number): string | undefined =>
      buildParametersPath
        ? buildParametersPath(buildSelectionParameters(normalInboxesSelection([conversationId])))
        : undefined,
    [buildSelectionParameters, buildParametersPath],
  );

  const onSelectionChange: HandlerOf<InboxesSelection<number>> = useCallback(
    (selection) => onParametersChange(buildSelectionParameters(selection)),
    [buildSelectionParameters, onParametersChange],
  );

  /*
   * Refs.
   */

  const firstCellRef = useRef<HTMLDivElement>(null);

  /*
   * Scroller.
   */

  const {cellsContext} = page;
  const {selection} = parameters;

  const focusedId = findOnlySelectedId(selection);
  const areHoverActionsEnabled = isDefined(focusedId);

  const renderItem = useCallback(
    (id: number, onClick: ConversationCellClickEventHandler) => {
      const index = conversationIndexes[id];
      const conversationSearchResult = conversationSearchResults[index];
      const isSelected = computeIsIdSelected(selection, conversationSearchResult.id);
      const maybeInnerRef = (index === 0 && firstCellRef) || undefined;

      return (
        <ConversationSearchResultCellContainer
          searchResult={conversationSearchResult}
          conversationHighlight={conversationSearchResult.mergedHighlights}
          cellsContext={cellsContext}
          parameters={parameters}
          isSelected={isSelected}
          customDragItem={isSelected ? props.selectedDragItem : undefined}
          customDragMessages={isSelected ? props.selectedDragMessages : undefined}
          path={buildConversationPath(id)}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          selection={selection}
          innerRef={maybeInnerRef}
          areHoverActionsEnabled={areHoverActionsEnabled}
        />
      );
    },
    [
      conversationIndexes,
      conversationSearchResults,
      selection,
      cellsContext,
      parameters,
      buildConversationPath,
      onDoubleClick,
      areHoverActionsEnabled,
      props.selectedDragItem,
      props.selectedDragMessages,
    ],
  );

  /*
   * Render.
   */

  return (
    <CellsItemsBase
      ref={ref}
      panelId={props.panelId}
      hasNextPage={props.hasNextPage}
      page={page}
      reselectDirection={props.reselectDirection}
      itemIds={conversationIds}
      itemIndexes={conversationIndexes}
      renderItem={renderItem}
      defaultId={0}
      focusedId={focusedId}
      onSelectionChange={onSelectionChange}
      onRetryClick={props.onRetryClick}
      onRequestMoreAsync={props.onRequestMoreAsync}
    >
      {(children, options) => (
        <>
          {props.children(children, options)}
          {firstCellRef.current && <ReadStatusInfoBubble targetRef={firstCellRef} />}
        </>
      )}
    </CellsItemsBase>
  );
});

/*
 * Helpers.
 */

function findSearchActivityForConversationSelection(
  searchResults: readonly ConversationSearchResultModel[],
  conversationIndexes: ItemIndexes,
  selection: InboxesSelection<number>,
): InboxesParametersActivity | undefined {
  // If we have more than one selected ID, nothing to do.
  const conversationId = findOnlySelectedId(selection);
  if (isUndefined(conversationId)) {
    return undefined;
  }

  // Otherwise, check if we have an activity ID to focus from the search highlight.
  const searchResultId = conversationIndexes[conversationId];
  const highlight = searchResults[searchResultId]?.mergedHighlights;
  const searchActivityId = highlight?.searchActivity?.id;
  if (isUndefined(searchActivityId)) {
    return undefined;
  }

  return {id: searchActivityId};
}
